<template>
  <q-dialog :model-value="modelValue" persistent no-route-dismiss @update:model-value="close">
    <q-card style="min-width: 380px; max-width: 520px" data-test="dialogo-transferir">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">{{ titulo }}</div>
        <q-space />
        <q-btn icon="close" flat round dense :disable="transferindo" data-test="fechar-transferencia" @click="close" />
      </q-card-section>

      <q-card-section>
        <q-banner v-if="aviso" dense class="bg-orange-1 text-orange-10 q-mb-md" data-test="aviso-fechamento">{{ aviso }}</q-banner>
        <q-banner v-if="!carregando && abertasOcultas" dense class="bg-orange-1 text-orange-10 q-mb-md" data-test="abertas-ocultas">{{ avisoOcultas }}</q-banner>
        <q-banner v-if="falha" dense class="bg-red-1 text-negative q-mb-md" data-test="falha-transferencia">{{ falha }}</q-banner>
        <div v-if="carregando" class="text-grey-7">Carregando caixas abertos...</div>
        <div v-else-if="erro" class="text-negative" data-test="erro-transferencia">{{ erro }}</div>
        <div v-else-if="!alvos.length && !abertasOcultas" class="text-grey-7" data-test="sem-vendas">Não há mais venda online em aberto neste caixa. Tente fechar de novo.</div>
        <div v-else-if="alvos.length && !destinos.length" class="text-grey-7" data-test="sem-destinos">
          Não há outro caixa aberto. Peça para abrirem um caixa antes de transferir.
        </div>
        <template v-else-if="alvos.length">
          <div class="q-mb-sm">Escolha o caixa que vai receber: o pagamento entra lá. A comissão continua com a operadora que fez a venda.</div>
          <q-list bordered separator>
            <q-item
              v-for="destino in destinos"
              :key="destino.id"
              clickable
              :disable="transferindo || destino.permitido === false"
              :active="escolhido && escolhido.id === destino.id"
              active-class="bg-blue-1 text-primary"
              :data-test="`destino-${destino.id}`"
              @click="choose(destino)"
            >
              <q-item-section avatar>
                <q-icon name="point_of_sale" color="primary" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ destino.caixa_nome }}</q-item-label>
                <q-item-label caption>{{ destino.operadora_nome }} · Sessão {{ destino.sessao }} · {{ formatData(destino.dtmovimento) }}</q-item-label>
                <q-item-label v-if="destino.permitido === false" caption class="text-orange-10">{{ destino.motivo }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </template>
      </q-card-section>

      <q-card-actions v-if="escolhido" align="right" class="q-px-md" data-test="confirmacao-transferencia">
        <div class="col text-weight-medium">{{ confirmacao }}</div>
        <q-btn flat no-caps label="Voltar" color="primary" :disable="transferindo" data-test="voltar-transferencia" @click="escolhido = null" />
        <q-btn unelevated no-caps label="Transferir" color="primary" :loading="transferindo" data-test="confirmar-transferencia" @click="transfer" />
      </q-card-actions>
      <q-card-actions v-else-if="aviso" align="right">
        <q-btn flat no-caps label="Ver vendas online" color="primary" data-test="ver-vendas-online" @click="openVendas" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { VENDA_ONLINE_ABERTAS, mensagemAbertasOcultas } from "@/utils/venda-online";

const LOTE = 50;

export default {
  name: "modal-transferir-vendas-online",
  props: {
    modelValue: { type: Boolean, default: false },
    caixaId: { type: [Number, String], required: true },
    vendas: { type: Array, default: null },
    aviso: { type: String, default: "" }
  },
  emits: ["update:modelValue", "transferidas"],
  data() {
    return {
      alvos: [],
      destinos: [],
      escolhido: null,
      carregando: true,
      transferindo: false,
      erro: null,
      falha: null,
      abertasOcultas: 0
    };
  },
  computed: {
    titulo() {
      if (this.alvos.length === 1) return `Transferir venda ${this.alvos[0].codigo}`;

      return this.alvos.length ? `Transferir ${this.alvos.length} vendas online` : "Transferir vendas online";
    },
    nomeDestino() {
      return this.escolhido ? `${this.escolhido.caixa_nome} (${this.escolhido.operadora_nome})` : "";
    },
    avisoOcultas() {
      return mensagemAbertasOcultas(this.abertasOcultas);
    },
    confirmacao() {
      const quais = this.alvos.length === 1 ? `a venda ${this.alvos[0].codigo}` : `${this.alvos.length} vendas`;

      return `Transferir ${quais} para o caixa ${this.nomeDestino}?`;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    formatData(data) {
      const [ano, mes, dia] = String(data || "").slice(0, 10).split("-");

      return dia ? `${dia}/${mes}/${ano}` : "";
    },
    async load() {
      try {
        if (this.vendas) {
          this.alvos = this.vendas;
        } else {
          const doCaixa = await this.$store.dispatch("fetchVendasOnlineCaixa", this.caixaId);
          if (!doCaixa.ok) {
            this.erro = doCaixa.mensagem;
            return;
          }
          this.alvos = doCaixa.vendas.filter((venda) => VENDA_ONLINE_ABERTAS.includes(venda.status));
          this.abertasOcultas = doCaixa.abertasOcultas || 0;
        }
        if (!this.alvos.length) return;

        const resultado = await this.$store.dispatch("fetchVendaOnlineDestinos", this.alvos[0].id);
        if (!resultado.ok) {
          this.erro = resultado.mensagem;
          return;
        }
        this.destinos = resultado.destinos;
      } finally {
        this.carregando = false;
      }
    },
    choose(destino) {
      if (destino.permitido === false) return;

      this.escolhido = destino;
    },
    async transfer() {
      if (!this.escolhido || this.transferindo) return;

      const nomeDestino = this.nomeDestino;
      const ids = this.alvos.map((venda) => venda.id);
      const resultados = [];
      let recusa = null;
      this.transferindo = true;
      this.falha = null;
      try {
        for (let inicio = 0; inicio < ids.length && !recusa; inicio += LOTE) {
          const lote = await this.$store.dispatch("transferVendasOnline", { ids: ids.slice(inicio, inicio + LOTE), movcaixaId: this.escolhido.id });
          if (lote.ok) {
            resultados.push(...lote.resultados);
          } else {
            recusa = lote.mensagem;
          }
        }
      } finally {
        this.transferindo = false;
      }

      const transferidas = resultados.filter((item) => item.resultado === "transferida");
      if (transferidas.length) {
        const movidas = transferidas.map((item) => item.id);
        this.$emit("transferidas", resultados);
        this.alvos = this.alvos.filter((venda) => !movidas.includes(venda.id));
      }
      if (recusa) {
        this.notifyRecusa(recusa, transferidas.length, ids.length);
        return;
      }

      this.notifyResultado(resultados, nomeDestino);
      if (!transferidas.length) {
        const falhas = resultados.filter((item) => item.resultado !== "transferida");
        this.falha = falhas.length ? falhas[0].mensagem : "Nenhuma venda foi transferida.";
        this.escolhido = null;
        return;
      }
      this.close();
    },
    notifyRecusa(mensagem, transferidas, total) {
      if (!transferidas) {
        this.notify(mensagem, "negative");
        return;
      }

      this.notify(`${transferidas} de ${total} vendas transferidas. ${mensagem}`, "orange");
    },
    notifyResultado(resultados, nomeDestino) {
      const falhas = resultados.filter((item) => item.resultado !== "transferida");
      const total = resultados.length;
      if (!falhas.length) {
        const feito = total === 1 ? `Venda transferida para o caixa ${nomeDestino}.` : `${total} vendas transferidas para o caixa ${nomeDestino}.`;
        this.notify(this.aviso ? `${feito} Agora o caixa pode ser fechado.` : feito, "green");
        return;
      }
      if (total === 1) {
        this.notify(falhas[0].mensagem, "negative");
        return;
      }

      const transferidas = total - falhas.length;
      this.notify(`${transferidas} de ${total} vendas transferidas. ${falhas[0].codigo}: ${falhas[0].mensagem}`, transferidas ? "orange" : "negative");
    },
    openVendas() {
      this.$router.push("/caixas/" + this.caixaId + "/vendas-online");
      this.close();
    },
    notify(message, color) {
      this.$q.notify({ message, color, position: "top-right" });
    },
    close() {
      if (this.transferindo) return;

      this.$emit("update:modelValue", false);
    }
  }
};
</script>
